import { cn } from '@yeshuman/sdk'
import { useMemo, useState, type HTMLAttributes, type ReactNode } from 'react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { StageColumnHeader } from './stage-column-header'

/** Column shell shared with the applicants board so every board looks the same. */
export const KANBAN_COLUMN_CLASS =
  'flex h-full w-[280px] shrink-0 flex-col overflow-hidden rounded-lg bg-muted/10'

export interface SimpleKanbanColumn {
  id: string
  label: string
  subtitle?: string
}

export type SimpleKanbanCardProps = {
  dragHandleProps?: HTMLAttributes<HTMLElement>
  isOverlay?: boolean
}

interface SimpleKanbanProps<T> {
  columns: SimpleKanbanColumn[]
  items: T[]
  getId: (item: T) => string
  getColumn: (item: T) => string
  renderCard: (item: T, props: SimpleKanbanCardProps) => ReactNode
  onMove: (item: T, toColumn: string) => void
  canDrop?: (item: T, toColumn: string) => boolean
  emptyLabel?: string
  ariaLabel: string
  className?: string
}

/** Drag cards between fixed columns; no in-column ordering. */
export function SimpleKanban<T>({
  columns,
  items,
  getId,
  getColumn,
  renderCard,
  onMove,
  canDrop,
  emptyLabel = 'Nothing here yet',
  ariaLabel,
  className,
}: SimpleKanbanProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  )
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const byId = useMemo(() => new Map(items.map((item) => [getId(item), item])), [items, getId])
  const byColumn = useMemo(() => {
    const grouped = new Map<string, T[]>(columns.map((column) => [column.id, []]))
    for (const item of items) grouped.get(getColumn(item))?.push(item)
    return grouped
  }, [columns, items, getColumn])

  const dragging = draggingId ? byId.get(draggingId) ?? null : null

  function handleDragEnd(event: DragEndEvent) {
    setDraggingId(null)
    const item = byId.get(String(event.active.id))
    const target = event.over ? String(event.over.id) : null
    if (!item || !target || target === getColumn(item)) return
    if (canDrop && !canDrop(item, target)) return
    onMove(item, target)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(event) => setDraggingId(String(event.active.id))}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDraggingId(null)}
    >
      <div
        className={cn('flex min-h-[420px] items-stretch gap-4 overflow-x-auto pb-3', className)}
        aria-label={ariaLabel}
      >
        {columns.map((column) => (
          <SimpleKanbanColumnView
            key={column.id}
            column={column}
            items={byColumn.get(column.id) ?? []}
            getId={getId}
            renderCard={renderCard}
            emptyLabel={emptyLabel}
            dropDisabled={dragging !== null && canDrop ? !canDrop(dragging, column.id) : false}
          />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {dragging ? <div className="w-[260px]">{renderCard(dragging, { isOverlay: true })}</div> : null}
      </DragOverlay>
    </DndContext>
  )
}

function SimpleKanbanColumnView<T>({
  column,
  items,
  getId,
  renderCard,
  emptyLabel,
  dropDisabled,
}: {
  column: SimpleKanbanColumn
  items: T[]
  getId: (item: T) => string
  renderCard: (item: T, props: SimpleKanbanCardProps) => ReactNode
  emptyLabel: string
  dropDisabled: boolean
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id, disabled: dropDisabled })
  return (
    <section
      ref={setNodeRef}
      className={cn(
        KANBAN_COLUMN_CLASS,
        'border border-border/40 transition-colors',
        isOver && 'border-foreground/30 bg-muted/30',
        dropDisabled && 'opacity-60',
      )}
      aria-label={`${column.label} column`}
    >
      <StageColumnHeader
        stage={{ slug: column.id, name: column.label, order: 0, is_terminal: false }}
        count={items.length}
        subtitle={column.subtitle}
        isOver={isOver}
      />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
        <div className="space-y-2 px-4 py-3">
          {items.length === 0 ? (
            <div className="flex min-h-24 items-center justify-center rounded-md border border-dashed border-border/40 px-3 text-center text-xs text-muted-foreground">
              {emptyLabel}
            </div>
          ) : (
            items.map((item) => (
              <DraggableCard key={getId(item)} id={getId(item)}>
                {(dragHandleProps) => renderCard(item, { dragHandleProps })}
              </DraggableCard>
            ))
          )}
        </div>
      </div>
    </section>
  )
}

function DraggableCard({
  id,
  children,
}: {
  id: string
  children: (dragHandleProps: HTMLAttributes<HTMLElement>) => ReactNode
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id })
  return (
    <div ref={setNodeRef} className={cn('transition-none', isDragging && 'opacity-40 touch-none')}>
      {children({ ...listeners, ...attributes })}
    </div>
  )
}
